import { breakpoints, colors, spacing } from '../../../utils/styleguide';
import styled from '@emotion/styled';
import Typography from '../text/Typography';
import Image from '../Image';
import { css } from '@emotion/react';
import PrimaryButton from '../buttons/PrimaryButton';
import SectionTitle from '../SectionTitle';
import Container from '../layout/Container';

const HeroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${spacing[4]}px;
  padding-top: ${spacing[1]}px;
  margin-bottom: ${spacing[4]}px;
  margin-top: ${spacing[9]}px;

  ${breakpoints.desktop} {
    flex-direction: row;
    gap: ${spacing[9]}px;
    padding-top: 0;
    margin-bottom: 0;
    margin-top: 0;
  }
`;

const LeftSideWrapper = styled.div`
  align-content: center;

  ${breakpoints.desktop} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 2;
  }
`;

const ContentWrapper = styled.div`
  margin-top: ${spacing[4]}px;
  padding-left: 0;

  ${breakpoints.desktop} {
    padding-left: ${spacing[5]}px;
  }
`;

const StyledImage = styled(Image)<{ overlay: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: ${({ overlay }) => (overlay ? 'brightness(0.8)' : 'none')};
  ${breakpoints.desktop} {
    height: 100%;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 180px;
  min-width: 35%;

  ${breakpoints.tablet} {
    height: 280px;
  }

  ${breakpoints.desktop} {
    flex: 1;
    height: 500px;
  }
`;

const ImageCreditWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: ${spacing[2]}px ${spacing[3]}px;
  background-color: ${colors.purple100};
`;

const ImageOverlayIconWrapper = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
`;

const ImageOverlayIconCss = css`
  max-width: 50%;
  min-width: 30%;
  filter: brightness(0) saturate(100%) invert(100%) sepia(10%) saturate(7437%) hue-rotate(290deg)
    brightness(119%) contrast(100%);
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;

  ${breakpoints.tablet} {
    flex-direction: row;
    gap: ${spacing[4]}px;
  }
`;

type Props = {
  primaryButtonLabel?: string;
  primaryButtonHref?: string;
  secondaryButtonLabel?: string;
  secondaryButtonHref?: string;
  imageUrl?: string;
  imageAlt?: string;
  imageCredit?: string;
  imageOverlayIconUrl?: string;
  imageOverlayIconAlt?: string;
  paragraph?: string;
  title: string;
};

export default function FundHero({
  title,
  paragraph,
  imageUrl,
  imageAlt,
  imageCredit,
  imageOverlayIconUrl,
  imageOverlayIconAlt,
  primaryButtonLabel,
  primaryButtonHref,
  secondaryButtonLabel,
  secondaryButtonHref,
}: Props) {
  return (
    <Container
      backgroundColor={colors.grey50}
      style={{ borderBottom: `1px solid ${colors.grey02}` }}
      disableVerticalPadding
    >
      <HeroWrapper>
        <LeftSideWrapper>
          <SectionTitle color={colors.blackPrimary} title={title} variant="h1" />

          <ContentWrapper>
            <Typography
              variant="h5"
              css={css`
                margin-bottom: ${spacing[6]}px;
              `}
            >
              {paragraph}
            </Typography>

            <ButtonWrapper>
              {primaryButtonLabel && primaryButtonHref && (
                <PrimaryButton
                  label={primaryButtonLabel}
                  variant="solid"
                  href={primaryButtonHref}
                />
              )}
              {secondaryButtonLabel && secondaryButtonHref && (
                <PrimaryButton
                  label={secondaryButtonLabel}
                  variant="outline"
                  href={secondaryButtonHref}
                />
              )}
            </ButtonWrapper>
          </ContentWrapper>
        </LeftSideWrapper>

        <ImageWrapper>
          {imageUrl && (
            <StyledImage
              url={imageUrl}
              maxWidth={{ desktop: 500 }}
              alt={imageAlt ?? title}
              overlay={!!imageOverlayIconUrl}
            />
          )}
          {imageUrl && imageOverlayIconUrl && (
            <ImageOverlayIconWrapper>
              <Image
                url={imageOverlayIconUrl}
                alt={imageOverlayIconAlt ?? ''}
                css={ImageOverlayIconCss}
              />
            </ImageOverlayIconWrapper>
          )}
          {imageUrl && imageCredit && (
            <ImageCreditWrapper>
              <Typography variant="overlineSmall">{imageCredit}</Typography>
            </ImageCreditWrapper>
          )}
        </ImageWrapper>
      </HeroWrapper>
    </Container>
  );
}
